import React, { useState, useEffect, useRef } from 'react'
import { isEmpty } from 'lodash'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import Select from '@/components/forms/Select'

import { MentionableTextarea } from './MentionableTextarea'
import { VisibilityRadio } from './VisibilityRadio'

import './NoteForm.scss'

/**
 * Form used for both new-note and edit-note. Textarea handles `@mention`
 * inline; the visibility radio + (optional) criteria picker + (optional)
 * notify-agent checkbox sit on a single control bar beneath.
 *
 * @param {object} props
 * @param {number} props.organizationId
 * @param {Array<object>} props.orgUsers
 * @param {'create'|'edit'} [props.mode='create']
 * @param {{noteText?: string, visibility?: string, mentionedUserIds?: number[], criteriaScoreId?: number, callMomentTimestamp?: number}} [props.initialValues]
 * @param {Array<{value: number, label: string}>} [props.criteriaOptions] - only for call notes wanting to pre-link a criterion
 * @param {boolean} [props.allowAgentVisible=true] - whether to surface the `agent_visible` option in the visibility picker. False for anchors where the gate isn't enforced (call, task, message). See `noteVisibility.js`.
 * @param {boolean} [props.notifyAgentEnabled=false] - gates the visual checkbox until TREN-271 ships the backend
 * @param {boolean} [props.autoFocus=false]
 * @param {string} [props.placeholder] - overrides the default textarea placeholder (e.g. the task drawer's coaching/compliance copy)
 * @param {boolean} [props.saveOnUnmount=false] - auto-save an in-progress draft when the form unmounts (e.g. closing a task drawer without an explicit Post). Create mode only.
 * @param {(payload: object) => void} props.onSubmit
 * @param {() => void} [props.onCancel]
 */
export const NoteForm = ({
  organizationId,
  orgUsers,
  mode = 'create',
  initialValues = {},
  criteriaOptions,
  allowAgentVisible = true,
  notifyAgentEnabled = false,
  autoFocus = false,
  placeholder: placeholderOverride,
  saveOnUnmount = false,
  onSubmit,
  onCancel,
}) => {
  const { noteMentions: mentionsEnabled = false } = useFlags()
  const [noteText, setNoteText] = useState(initialValues.noteText || '')
  // When the anchor no longer surfaces the Agent option but the loaded
  // note was already stored as agent_visible, coerce to public on
  // mount. Otherwise the segmented control renders no active item
  // (neither Public nor Private matches the stored value) and a save
  // without a deliberate visibility change would silently re-submit
  // the deprecated agent_visible value. Aligns with the "hide, not
  // disable" intent — once the option is gone, edits phase the value
  // out the next time someone touches the note.
  const initialVisibility =
    initialValues.visibility === 'agent_visible' && !allowAgentVisible
      ? 'public'
      : initialValues.visibility || 'public'
  const [visibility, setVisibility] = useState(initialVisibility)
  const [mentionedUserIds, setMentionedUserIds] = useState(initialValues.mentionedUserIds || [])
  const [criteriaScoreId, setCriteriaScoreId] = useState(initialValues.criteriaScoreId || null)
  const [notifyAgent, setNotifyAgent] = useState(false)

  const canSubmit = !isEmpty(noteText.trim())

  let placeholder = placeholderOverride || 'Add a note…'
  if (mode === 'edit') {
    placeholder = 'Edit your note…'
  } else if (mentionsEnabled && !placeholderOverride) {
    placeholder = 'Add a note… type @ to mention someone'
  }

  const handleSubmit = () => {
    if (!canSubmit) {
      return
    }
    onSubmit({
      noteText: noteText.trim(),
      visibility,
      mentionedUserIds,
      criteriaScoreId,
      notifyAgent,
    })
    // Always-mounted callers (collapsible={false}: CallNotesTab compose,
    // playlist/call drawer) keep this form rendered after a create, so reset
    // the fields — otherwise the posted text lingers and a stray click reposts
    // it. The collapsible path unmounts on submit, so this is a no-op there.
    if (mode === 'create') {
      setNoteText('')
      setMentionedUserIds([])
      setCriteriaScoreId(null)
      setNotifyAgent(false)
    }
  }

  // saveOnUnmount: task drawers auto-save an in-progress draft when the drawer
  // closes without an explicit Post (ported from the legacy Comments
  // component). Keep the latest values + onSubmit in refs so the once-only
  // unmount cleanup sees current state, not the mount-time snapshot. The
  // create-submit reset above clears noteText, so an explicit Post leaves
  // nothing for this to re-save.
  const draftRef = useRef(null)
  draftRef.current = { noteText, visibility, mentionedUserIds, criteriaScoreId, notifyAgent }
  const onSubmitRef = useRef(onSubmit)
  onSubmitRef.current = onSubmit
  useEffect(() => {
    if (!saveOnUnmount || mode !== 'create') {
      return undefined
    }
    return () => {
      const draft = draftRef.current
      if (!isEmpty(draft.noteText.trim())) {
        onSubmitRef.current({ ...draft, noteText: draft.noteText.trim() })
      }
    }
  }, [saveOnUnmount, mode])

  return (
    <div className="note-form" data-testid={`note-form-${mode}`}>
      <div className="note-form__audience">
        <VisibilityRadio
          value={visibility}
          onChange={setVisibility}
          allowAgentVisible={allowAgentVisible}
        />
      </div>

      <MentionableTextarea
        value={noteText}
        onChange={setNoteText}
        mentionedUserIds={mentionedUserIds}
        onMentionedChange={setMentionedUserIds}
        organizationId={organizationId}
        orgUsers={orgUsers}
        autoFocus={autoFocus}
        placeholder={placeholder}
        testId="note-form-textarea"
      />

      <div className="note-form__controls">
        {!isEmpty(criteriaOptions) && (
          <Select
            autoWidth
            data-testid="note-form-criteria"
            aria-label="Link to criteria"
            placeholder="Link to criteria…"
            options={criteriaOptions}
            value={criteriaScoreId}
            onChange={(option) => setCriteriaScoreId(option ? option.value : null)}
          />
        )}

        {notifyAgentEnabled && (
          <Checkbox
            label="Notify agent"
            data-testid="note-form-notify-agent"
            checked={notifyAgent}
            onChange={(event, data) => setNotifyAgent(data.checked)}
          />
        )}

        <div className="note-form__spacer" />

        {onCancel && (
          <Button small onClick={onCancel} data-testid="note-form-cancel">
            Cancel
          </Button>
        )}
        <Button
          brand
          small
          disabled={!canSubmit}
          onClick={handleSubmit}
          data-testid="note-form-save"
        >
          {mode === 'edit' ? 'Save' : 'Post'}
        </Button>
      </div>
    </div>
  )
}
